import { BloodGlucoseForm } from "@/model/BloodGlucoseForm";
import BloodGlucoseFormContext from "@/model/BloodGlucoseFormContext";
import { BloodGlucoseObservation } from "@/model/BloodGlucoseObservation";
import cn from "@/util/cn";
import { DateTimeFormatter, Instant, LocalDate, ZoneId } from "@js-joda/core";
import { Locale } from "@js-joda/locale";
import { findLast } from "lodash";
import last from "lodash/last";
import { ComponentPropsWithoutRef, Fragment } from "react";
import { Cell, HeaderCell } from "../../Cell";
import FoodLogCell from "./FoodLogCell";

type FoodLogTableProps = ComponentPropsWithoutRef<"div"> & {
  dates: LocalDate[];
  observations: BloodGlucoseObservation[];
  lastReviewTime: Instant;
  selectedObservation: BloodGlucoseObservation | null;
  onObservationSelected: (observation: BloodGlucoseObservation) => void;
};

const FoodLogTable = ({
  dates,
  observations,
  lastReviewTime,
  selectedObservation,
  onObservationSelected,
  className,
  ...otherProps
}: FoodLogTableProps) => {
  const mealContexts = [
    "breakfast",
    "snack-between-breakfast-and-lunch",
    "lunch",
    "snack-between-lunch-and-dinner",
    "dinner",
    "snack-after-dinner",
  ] as const;

  const isSelectedObservation = (observation: BloodGlucoseObservation | undefined) =>
    selectedObservation !== null && observation?.id === selectedObservation.id;

  return (
    <div className={cn("ps-px", className)} {...otherProps}>
      <div className="grid grid-cols-[min-content_1fr_1fr_1fr_1fr_1fr_1fr] gap-px">
        <Cell />
        <HeaderCell>Breakfast</HeaderCell>
        <HeaderCell>Snack</HeaderCell>
        <HeaderCell>Lunch</HeaderCell>
        <HeaderCell>Snack</HeaderCell>
        <HeaderCell>Dinner</HeaderCell>
        <HeaderCell>Snack</HeaderCell>
        {dates.map((date, i) => {
          const dateString = dayMonthFormatter.format(date);
          const dayOfWeekString = dayOfWeekFormatter.format(date);
          const dateStart = date.atStartOfDay(ZoneId.SYSTEM).toInstant();
          return (
            <Fragment key={`reading-${i}`}>
              <HeaderCell className="justify-start">
                <span className="inline-block" style={{ minWidth: `${dateString.length}ch` }}>
                  {dateString}
                </span>
                {dayOfWeekString}
              </HeaderCell>
              {mealContexts.map((mealContext) => {
                const observation = observationForCell({
                  observations,
                  date,
                  mealContext: mealContext,
                });
                const isNew = observation?.dateTime.toInstant().isAfter(lastReviewTime);
                const isEmpty = !observation;
                const isDisabled = isEmpty && dateStart.isAfter(Instant.now());
                const status = isDisabled ? "disabled" : isNew ? "new" : "default";
                return (
                  <FoodLogCell
                    key={`reading-cell-${date}-${mealContext}`}
                    observation={observation}
                    status={status}
                    onClick={() => {
                      if (!isEmpty) {
                        onObservationSelected(observation!);
                      }
                    }}
                    className={cn(
                      isEmpty ? "cursor-default" : "cursor-pointer",
                      isEmpty
                        ? ""
                        : isSelectedObservation(observation)
                          ? "ring-2 ring-inset ring-blue-400 print:ring-transparent"
                          : "ring-blue-400 hover:ring-2 hover:ring-inset hover:ring-blue-400/50 print:ring-transparent print:hover:ring-transparent"
                    )}
                  />
                );
              })}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

const dayOfWeekFormatter = DateTimeFormatter.ofPattern("EEEE").withLocale(Locale.US);
const dayMonthFormatter = DateTimeFormatter.ofPattern("MM/dd").withLocale(Locale.US);

const observationForCell = ({
  observations,
  date,
  mealContext,
}: {
  observations: BloodGlucoseObservation[];
  date: LocalDate;
  mealContext: BloodGlucoseFormContext;
}): (BloodGlucoseObservation & { form: BloodGlucoseForm }) | undefined => {
  const observationsWithMealInfo = observations
    .filter((observation) => observation.form?.context === mealContext)
    .filter((observation) => hasMealInfo(observation))
    .filter((observation) => observation.dateTime.toLocalDate().equals(date))
    .map((observation) => ({ ...observation, form: observation.form! }));
  return (
    findLast(observationsWithMealInfo, (observation) => observation.reading !== undefined) ?? // Prefer observations with readings
    last(observationsWithMealInfo)
  );
};

const hasMealInfo = (observation: BloodGlucoseObservation) => {
  const { mealContents, carbsInGrams, proteinInGrams } = observation.form ?? {};
  return mealContents !== undefined || carbsInGrams !== undefined || proteinInGrams !== undefined;
};

export default FoodLogTable;
